import { useState, useCallback, useMemo, useRef } from 'react';
import { useField } from 'formik';

import { getSearchEntryError, isBooleanSearch } from '../../../../../../helpers/booleanValidator';
import { useJobTitleFilter } from './useJobTitleFilter';

const trimValue = (value) => (typeof value === 'string' ? value.trim() : '');

const trimEntry = (item) =>
  typeof item.value === 'string' ? { ...item, value: item.value.trim(), label: trimValue(item.label) } : item;

export const useFormikJobTitleMultiSelect = ({ name, sharedList, orderBy }) => {
  const [field, , helpers] = useField(name);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const selectRef = useRef(null);

  const { onSearchChange } = useJobTitleFilter({
    sharedList,
    orderBy,
    setFilters: () => {},
  });

  const selected = useMemo(
    () =>
      (field.value || []).map((item) => ({
        ...item,
        isEditable: isBooleanSearch(item.value),
      })),
    [field.value],
  );

  const handleEditValue = useCallback(
    (data) => {
      // Remove the item and put its value in the input for editing
      const newValues = (field.value || []).filter((item) => item.value !== data.value);
      helpers.setValue(newValues);
      setInputValue(data.label);
      // The entry may predate the cap, so judge what lands in the input.
      setError(getSearchEntryError(trimValue(data.label)));

      // Focus the select
      if (selectRef.current) {
        selectRef.current.focus();
      }
    },
    [field.value, helpers],
  );

  const handleChange = useCallback(
    (e) => {
      // react-select builds a created option from the raw input, so normalize
      // the new/edited entry: what gets validated below is then exactly what
      // gets submitted. Entries already selected (hydrated from saved/shared
      // filters) pass through untouched — their stored value is not ours to
      // rewrite on an unrelated edit.
      const value = e.target.value.map((item) =>
        selected.some((current) => current.value === item.value) ? item : trimEntry(item),
      );

      // Anything not already selected is new or edited, whatever its shape —
      // keying off the list length or isBooleanSearch misses an edit that
      // replaces a boolean entry with a plain one.
      const changedEntries = value.filter((item) => !selected.some((current) => current.value === item.value));
      const entryError = changedEntries.map((item) => getSearchEntryError(item.value)).find(Boolean) || null;

      if (entryError) {
        setError(entryError);
        return;
      }

      const booleanSearch = selected.find((item) => item.isEditable);
      const addedBooleanSearch = value.length > selected.length && isBooleanSearch(value[value.length - 1].value);

      // Only allow one boolean search at a time
      const newValue =
        addedBooleanSearch && booleanSearch ? value.filter((item) => item.value !== booleanSearch.value) : value;

      setError(null);
      setInputValue('');
      helpers.setValue(newValue);
    },
    [selected, helpers],
  );

  const handleInputChange = useCallback((newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newValue);
      setError(getSearchEntryError(trimValue(newValue)));
    }
    if (actionMeta.action === 'set-value' || actionMeta.action === 'menu-close') {
      setInputValue('');
      setError(null);
    }
  }, []);

  const isValidNewOption = useCallback((inputVal) => {
    const trimmed = trimValue(inputVal);
    if (!trimmed) return false;

    const entryError = getSearchEntryError(trimmed);
    setError(entryError);

    return !entryError;
  }, []);

  const loadOptions = useCallback(
    async (inputVal) => {
      const trimmed = trimValue(inputVal);
      const entryError = getSearchEntryError(trimmed);
      setError(entryError);

      // Don't search the API for boolean queries or entries we would reject
      if (entryError || isBooleanSearch(trimmed)) {
        return [];
      }

      return onSearchChange(trimmed);
    },
    [onSearchChange],
  );

  return {
    field,
    selected,
    selectRef,
    error,
    inputValue,
    handleEditValue,
    handleChange,
    handleInputChange,
    isValidNewOption,
    loadOptions,
  };
};
